import { cx } from 'linaria';
import * as React from 'react';

import { Image } from '../index';
import * as styles from './avatar.styles';

export type SizesVariant = 'extra-small' | 'small' | 'medium' | 'large' | 'extra-large';
export type AvatarShape = 'rounded' | 'square';
export interface AvatarProps extends Omit<React.HTMLAttributes<HTMLImageElement>, 'onLoad' | 'onError' | 'onClick'> {
    /**
     *  Source of the image avatar
     */
    src?: string;
    /**
     *  Name of the person/team/organisation
     */
    name: string;
    /**
     *  Size of the avatar
     *  @default medium
     */
    size?: SizesVariant;
    /**
     *  Shape of the avatar
     *  @default rounded
     */
    shape?: AvatarShape;
    /**
     *  Click event on the avatar
     */
    onClick?: () => void;
    /**
     *  Test id for unit and e2e
     */
    testId?: string;
    /**
     *  key for the avatar
     */
    key?: React.Key | null | undefined;
}

const SIZES = {
    'extra-small': 16,
    small: 24,
    medium: 32,
    large: 48,
    'extra-large': 64,
};

export const Avatar = React.forwardRef<HTMLImageElement, AvatarProps>((props, ref) => {
    const {
        src,
        name,
        size = 'medium',
        shape = 'rounded',
        onClick,
        testId,
        key,
        'aria-pressed': ariaPressed,
        ...passThroughProps
    } = props;

    const initials = React.useMemo(() => {
        if (!name || name.trim().length === 0) {
            return '';
        }

        // return first letter
        return name.trim()[0];
    }, [name]);

    const fallback = () => <>{initials}</>;

    const backgroundColor = React.useMemo(() => calculateBackgroundColor(name), [name]);
    const className = cx(styles.root, styles.sizes[size], styles.shapes[shape], !onClick && passThroughProps.className);

    const avatarContent = (
        <Image
            {...passThroughProps}
            ref={ref}
            src={src}
            alt={name}
            color={backgroundColor}
            fallbackSlot={fallback}
            animation={false}
            testId={testId}
            key={key}
            height={SIZES[size]}
            width={SIZES[size]}
            className={className}
        />
    );

    if (!onClick) {
        return avatarContent;
    }

    return (
        <button
            className={cx(styles.button, styles.shapes[shape], passThroughProps.className)}
            type="button"
            onClick={onClick}
            aria-pressed={ariaPressed}
        >
            {avatarContent}
        </button>
    );
});

Avatar.displayName = 'Avatar';

// Helper

export type HSLTuple = [number, number, number];

export function calculateBackgroundColorTuple(string?: string): HSLTuple {
    if (!string || string.length === 0) {
        return null;
    }

    const color = string.split('').reduce((sum, char) => char.charCodeAt(0) + ((sum << 8) - sum), 0);
    const hue = color % 360;

    return [hue, 40, 30];
}

export function calculateBackgroundColor(string?: string): string {
    const [hue, saturation, lightness] = calculateBackgroundColorTuple(string);

    return `hsla(${hue}, ${saturation}%, ${lightness}%, var(--avatar-background-opacity, 1))`;
}
